import { Box, CircularProgress } from '@mui/material';
import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="var(--app-height, 100vh)"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    // Reason: preserve the destination so we can return after login.
    const next = `${location.pathname}${location.search}${location.hash}`;
    const shouldPreserve = next && next !== '/' && !next.startsWith('/login');
    const loginPath = shouldPreserve
      ? `/login?next=${encodeURIComponent(next)}`
      : '/login';
    return <Navigate to={loginPath} replace />;
  }

  if (user.consent_required && location.pathname !== '/consent') {
    // Reason: users missing required consent must complete re-consent first.
    return <Navigate to="/consent" replace />;
  }

  // Reason: users with stale Provider Association T&Cs / Privacy must
  // re-accept before reaching any other Provider feature. Mirrors the
  // user-level consent gate above. The /provider-consent route is the
  // only one allowed through.
  if (
    user.provider_terms_required
    && location.pathname !== '/provider-consent'
    && location.pathname !== '/consent'
  ) {
    return <Navigate to="/provider-consent" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
